/**
 * CREATIVE DETONATOR - Unified Auth Initialization
 * v2 - Single pattern for protected page initialization
 *
 * v2 FIX: Content container must be unhidden in ALL paths (already-auth, skip-auth, post-login)
 *
 * Consolidates duplicate auth check logic from generator.js and dashboard/core.js
 * into a single reusable function.
 *
 * Usage:
 *   import { initGeneratorAuth, initDashboardAuth } from './auth/auth-init.js';
 *
 *   // Generator page
 *   await initGeneratorAuth(async (user, profile) => {
 *     await initializeGenerator();
 *   });
 *
 *   // Dashboard page
 *   await initDashboardAuth(initDashboard, showLogin, showDashboard);
 */

import { authState } from './state.js';
import { initInlineAuth, isAuthenticated, showAuthScreen, hideAuthScreen } from './inline-auth.js';

// ============================================================================
// MAIN INITIALIZATION FUNCTION
// ============================================================================

/**
 * Initialize protected page with auth check
 *
 * @param {Object} config - Page configuration
 * @param {string} config.authScreenId - ID of auth/login screen element
 * @param {string} config.contentScreenId - ID of main content element
 * @param {string} config.formId - ID of login form
 * @param {string} config.emailId - ID of email input
 * @param {string} config.passwordId - ID of password input
 * @param {string} config.errorId - ID of error message element
 * @param {string} config.submitId - ID of submit button
 * @param {string} config.toggleId - ID of password toggle button
 * @param {string} config.googleId - ID of Google OAuth button
 * @param {Function} [config.onAuthenticated] - Called when user is already authenticated
 * @param {Function} [config.onLoginSuccess] - Called after successful login
 * @param {Function} [config.showAuthScreen] - Custom function to show auth screen
 * @param {Function} [config.hideAuthScreen] - Custom function to hide auth screen
 * @param {boolean} [config.requireAuth=true] - Whether auth is required
 * @returns {Promise<{authenticated: boolean, skipped: boolean}>}
 */
export async function initProtectedPage(config) {
  const {
    authScreenId,
    contentScreenId,
    formId,
    emailId,
    passwordId,
    errorId,
    submitId,
    toggleId,
    googleId,
    onAuthenticated,
    onLoginSuccess,
    showAuthScreen: customShowAuthScreen,
    hideAuthScreen: customHideAuthScreen,
    requireAuth = true,
  } = config;

  // SECURITY: Only check requireAuth parameter, not window.FEATURE_FLAGS
  // window.FEATURE_FLAGS can be manipulated via XSS or DOM clobbering
  if (!requireAuth) {
    console.log('[AuthInit] Auth not required by config, skipping check');

    // Still need to show content even when skipping auth
    if (customHideAuthScreen) {
      customHideAuthScreen();
    } else {
      hideAuthScreen(authScreenId, contentScreenId);
    }

    if (onAuthenticated) {
      await onAuthenticated(authState.user, authState.profile);
    }
    return { authenticated: true, skipped: true };
  }

  // Check authentication status
  try {
    const isAuthed = await isAuthenticated();

    if (isAuthed) {
      console.log('[AuthInit] User is authenticated');

      // FIX: Ensure content is visible immediately (unhide content container)
      if (customHideAuthScreen) {
        customHideAuthScreen();
      } else {
        hideAuthScreen(authScreenId, contentScreenId);
      }

      if (onAuthenticated) {
        await onAuthenticated(authState.user, authState.profile);
      }
      return { authenticated: true, skipped: false };
    }
  } catch (err) {
    console.warn('[AuthInit] Auth check failed:', err);
    // Continue to show login form
  }

  console.log('[AuthInit] User not authenticated, showing login form');

  // Show auth/login screen
  if (customShowAuthScreen) {
    customShowAuthScreen();
  } else {
    showAuthScreen(authScreenId, contentScreenId);
  }

  // Initialize inline auth form
  initInlineAuth({
    formId,
    emailId,
    passwordId,
    errorId,
    submitId,
    toggleId,
    googleId,
    onSuccess: async (user) => {
      console.log('[AuthInit] Login successful');

      // Hide auth screen, show content
      if (customHideAuthScreen) {
        customHideAuthScreen();
      } else {
        hideAuthScreen(authScreenId, contentScreenId);
      }

      // Refresh auth state
      await authState.init();

      // Call success callback
      if (onLoginSuccess) {
        await onLoginSuccess(authState.user, authState.profile);
      }
    }
  });

  return { authenticated: false, skipped: false };
}

// ============================================================================
// CONVENIENCE PRESETS
// ============================================================================

/**
 * Generator page auth preset
 *
 * @param {Function} onInit - Callback for when user is authenticated (initial or after login)
 * @returns {Promise<{authenticated: boolean, skipped: boolean}>}
 *
 * @example
 * await initGeneratorAuth(async (user, profile) => {
 *   setUser(user, profile);
 *   await initializeGenerator();
 * });
 */
export function initGeneratorAuth(onInit) {
  return initProtectedPage({
    authScreenId: 'auth-required-screen',
    contentScreenId: 'generator-content',
    formId: 'inline-login-form',
    emailId: 'inline-email',
    passwordId: 'inline-password',
    errorId: 'inline-error-message',
    submitId: 'inline-submit-btn',
    toggleId: 'inline-toggle-password',
    googleId: 'inline-google-login',
    onAuthenticated: onInit,
    onLoginSuccess: onInit,
  });
}

/**
 * Dashboard page auth preset
 *
 * @param {Function} onInit - Callback for when user is authenticated
 * @param {Function} [showFn] - Custom function to show login screen
 * @param {Function} [hideFn] - Custom function to hide login screen
 * @returns {Promise<{authenticated: boolean, skipped: boolean}>}
 *
 * @example
 * await initDashboardAuth(initDashboard, showLogin, showDashboard);
 */
export function initDashboardAuth(onInit, showFn, hideFn) {
  return initProtectedPage({
    authScreenId: 'dashboard-auth-screen',
    contentScreenId: 'dashboard-app',
    formId: 'dashboard-login-form',
    emailId: 'dashboard-email',
    passwordId: 'dashboard-password',
    errorId: 'dashboard-error-message',
    submitId: 'dashboard-submit-btn',
    toggleId: 'dashboard-toggle-password',
    googleId: 'dashboard-google-login',
    onAuthenticated: onInit,
    onLoginSuccess: onInit,
    showAuthScreen: showFn,
    hideAuthScreen: hideFn,
  });
}

// ============================================================================
// GLOBAL EXPORT (for non-module usage)
// ============================================================================

if (typeof window !== 'undefined') {
  window.AuthInit = {
    initProtectedPage,
    initGeneratorAuth,
    initDashboardAuth,
  };
}
